import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useReducedMotion } from "framer-motion";
import {
  Bell,
  BellOff,
  CheckCheck,
  ChevronRight,
  ArrowUpRight,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/contexts/AuthContext";
import { getNotifications, markNotificationsRead } from "@/lib/api";
import { formatNotificationTime, notificationDestination, notificationIcon } from "@/lib/notification-meta";
import type { NotificationRow } from "@tickethall/core";
import { cn } from "@/lib/utils";

interface NotificationBellProps {
  className?: string;
}

export function NotificationBell({ className }: NotificationBellProps) {
  const { t, i18n } = useTranslation(["producerCommon", "common"]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const shouldReduceMotion = useReducedMotion();
  const [open, setOpen] = useState(false);

  const { data: notifications } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: () => getNotifications(user!.id),
    enabled: !!user?.id,
    refetchInterval: 5 * 60000,
    staleTime: 60000,
  });

  const markRead = useMutation({
    mutationFn: () => markNotificationsRead(user!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["notifications-page", user?.id] });
    },
  });

  const unreadCount = notifications?.filter((n) => !n.is_read).length || 0;

  const handleOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    // Ao fechar o menu após visualização, se havia não lidas, marca como lidas
    if (!isOpen && unreadCount > 0) {
      markRead.mutate();
    }
  };

  const handleItemClick = (n: NotificationRow, destination: string) => {
    if (!n.is_read) {
      markRead.mutate();
    }
    setOpen(false);
    navigate(destination);
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("notificationBell.header.title", "Notificações")}
          className={cn(
            "relative flex items-center justify-center h-10 w-10 rounded-full border border-border/50 bg-secondary/40 hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20 cursor-pointer text-foreground/80 hover:text-foreground",
            "transition-[transform,background-color,border-color,color] duration-150 ease-out",
            !shouldReduceMotion && "active:scale-[0.97]",
            className
          )}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-background shadow-xs">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        aria-label="Central de notificações"
        className="w-[360px] sm:w-[390px] max-w-[calc(100vw-1.5rem)] p-0 rounded-[24px] border border-border/60 bg-background/95 backdrop-blur-2xl shadow-xl shadow-black/5 overflow-hidden outline-none z-[120]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-border/40 bg-muted/20 backdrop-blur-md">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-semibold text-sm tracking-[-0.02em] text-foreground">
              {t("notificationBell.header.title", "Notificações")}
            </h3>
            {unreadCount > 0 ? (
              <span className="inline-flex items-center h-5 px-2 rounded-full text-[11px] font-semibold bg-primary/10 text-primary tabular-nums">
                {t("notificationBell.unreadCount", { count: unreadCount })}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground/80">
                <CheckCheck className="h-3.5 w-3.5 text-emerald-500" />
                {t("notificationBell.allRead", "Em dia")}
              </span>
            )}
          </div>

          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markRead.mutate()}
              disabled={markRead.isPending}
              className={cn(
                "inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-background/80 rounded-full cursor-pointer",
                "transition-[background-color,color,transform] duration-150 ease-out",
                !shouldReduceMotion && "active:scale-[0.97]"
              )}
              title={t("notificationBell.markAllRead", "Marcar todas como lidas")}
            >
              <CheckCheck className="h-3.5 w-3.5 text-primary" />
              <span>{t("notificationBell.markReadShort", "Marcar lidas")}</span>
            </button>
          )}
        </div>

        {/* Scrollable notifications list */}
        <ScrollArea className="max-h-[380px] overflow-hidden">
          {!notifications || notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <div className="h-10 w-10 rounded-full border border-border/70 bg-card flex items-center justify-center mb-3 text-muted-foreground/60 shadow-xs">
                <BellOff className="h-4 w-4" />
              </div>
              <p className="font-display font-medium text-sm text-foreground tracking-[-0.01em]">
                {t("notificationBell.empty.message", "Nenhuma notificação por enquanto")}
              </p>
              <p className="text-xs text-muted-foreground mt-1 max-w-[240px] leading-relaxed">
                {t(
                  "notificationBell.empty.desc",
                  "Você receberá avisos sobre seus ingressos, mensagens e pedidos aqui."
                )}
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {notifications.map((n) => {
                const Icon = notificationIcon(n);
                const destination = notificationDestination(n);
                const isUnread = !n.is_read;
                const timeAgo = formatNotificationTime(n.created_at, i18n.language);

                return (
                  <button
                    type="button"
                    key={n.id}
                    onClick={() => handleItemClick(n, destination)}
                    className={cn(
                      "group relative flex items-start gap-3 p-3 rounded-[18px] text-left w-full select-none cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20",
                      "transition-[transform,background-color,border-color,color] duration-150 ease-out",
                      !shouldReduceMotion && "active:scale-[0.97]",
                      isUnread
                        ? "bg-secondary/60 hover:bg-secondary/90 border border-border/40"
                        : "hover:bg-secondary/40 border border-transparent"
                    )}
                  >
                    {/* Category Icon Container */}
                    <div
                      className={cn(
                        "h-9 w-9 rounded-2xl flex items-center justify-center shrink-0 border border-border/60 bg-secondary/40 text-muted-foreground shadow-xs",
                        "transition-transform duration-150 ease-out",
                        !shouldReduceMotion && "group-hover:scale-105"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-foreground tracking-tight line-clamp-1">
                          {n.title}
                        </span>
                        <span className="text-[10px] text-muted-foreground/70 shrink-0 font-medium tabular-nums">
                          {timeAgo}
                        </span>
                      </div>

                      {n.body && (
                        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed mt-0.5 font-normal">
                          {n.body}
                        </p>
                      )}
                    </div>

                    {/* 6px Coral Accent Dot (unread) or Subtle Chevron */}
                    <div className="flex items-center self-center shrink-0">
                      {isUnread ? (
                        <span className="h-1.5 w-1.5 rounded-full bg-primary ring-2 ring-primary/20 shrink-0" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[transform,opacity] duration-150 ease-out text-muted-foreground/50 group-hover:text-foreground/70" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-2 border-t border-border/40 bg-muted/20 backdrop-blur-md">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/notificacoes");
            }}
            className={cn(
              "flex items-center justify-center gap-1.5 w-full py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-background/80 rounded-full cursor-pointer",
              "transition-[background-color,color,transform] duration-150 ease-out",
              !shouldReduceMotion && "active:scale-[0.98]"
            )}
          >
            <span>{t("notificationBell.viewAll", "Ver todas as notificações")}</span>
            <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground/70" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


